import { createSelector } from 'reselect';
import isNil from 'ramda/src/isNil';
import CampaignService from 'mangools-commons/dist/services/CampaignService';
import DateTimeService from 'mangools-commons/dist/services/DateTimeService';
import Defaults from 'mangools-commons/dist/constants/Defaults';
import UserPlanTypes from 'constants/UserPlanTypes';

export const accessTokenSelector = state => state.user.accessToken;

export const createdAtSelector = state => state.user.createdAt;
export const fetchedSelector = state => state.user.fetched;
export const idSelector = state => state.user.id;
export const importLimitSelector = state => state.user.limits.importLimit;
export const limitsFetchedSelector = state => state.user.limits.fetched;
export const limitsSelector = state => state.user.limits;
export const loggedInSelector = state => state.user.loggedIn;
export const emailSelector = state => state.user.email;
export const ssoTicketSelector = state => state.user.ssoTicket;
export const userPlanSelector = state => state.user.plan;
export const loginTokenSelector = state => state.user.loginToken;
export const isSubUserSelector = state => state.user.isSubUser;
export const serpRequestRemainingSelector = state => state.user.limits.serpRequestRemaining;

export const userPlanTypeSelector = createSelector([loggedInSelector, userPlanSelector], (loggedIn, userPlan) => {
    if (loggedIn === false || isNil(userPlan)) {
        return UserPlanTypes.NO_PLAN;
    } else {
        switch (userPlan) {
            case 'free': {
                return UserPlanTypes.FREE;
            }
            case 'trial': {
                return UserPlanTypes.TRIAL;
            }
            case 'entry': {
                return UserPlanTypes.ENTRY;
            }
            case 'basic': {
                return UserPlanTypes.BASIC;
            }
            case 'premium': {
                return UserPlanTypes.PREMIUM;
            }
            case 'agency': {
                return UserPlanTypes.AGENCY;
            }
            case 'ai_premium': {
                return UserPlanTypes.AI_PREMIUM;
            }
            case 'api_starter': {
                return UserPlanTypes.FREE;
            }
            case 'api_pro': {
                return UserPlanTypes.FREE;
            }
            case 'custom': {
                return UserPlanTypes.CUSTOM;
            }
            default: {
                // NOTE: Should never happen
                return UserPlanTypes.NO_PLAN;
            }
        }
    }
});

export const userDropdownPlanSelector = createSelector(
    [userPlanSelector, userPlanTypeSelector],
    (userPlan, userPlanType) => (['api_starter', 'api_pro'].includes(userPlan) ? userPlan : userPlanType),
);

export const timerLimitSecondsSelector = createSelector(
    [loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return 15 * 60;

        // Only trial user can see this subscribe message, every new user will be TRIAL user
        if (loggedIn && !isNil(createdAt) && userPlanType === UserPlanTypes.TRIAL) {
            return DateTimeService.getTimeDifferenceInSeconds(
                createdAt + Defaults.USER_FRESH_LIMIT_MINUTES * 60 * 1000,
            );
        } else {
            return 0;
        }
    },
);

// Reads the clock when the user changes, so a campaign boundary shows after the next reload.
export const campaignBannerSelector = createSelector(
    [loggedInSelector, userPlanSelector, createdAtSelector],
    (loggedIn, plan, createdAt) =>
        CampaignService.getBanner({
            now: Date.now(),
            loggedIn,
            plan,
            createdAt,
            preview: CampaignService.readPreview(),
        }),
);

export const newUserMessageVisibilitySelector = createSelector(
    [campaignBannerSelector, loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (campaignBanner, loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return true;

        if (!isNil(campaignBanner)) {
            return true;
        }

        // Only trial user can see this subscribe message, every new user will be TRIAL user
        if (loggedIn && !isNil(createdAt) && userPlanType === UserPlanTypes.TRIAL) {
            return DateTimeService.lessThanMinutesAgo(Defaults.USER_FRESH_LIMIT_MINUTES, createdAt);
        } else {
            return false;
        }
    },
);

export const unleashSessionSelector = state => state.user.unleashSession;
